/**
 * auth-state.ts - Authentication state management
 */

import type { User } from '../../types/auth';
import { checkAuthStatus as checkAuthStatusAPI } from './auth-api';

/**
 * Global state for current user
 */
let currentUser: User | null = null;

/**
 * Get current user
 * @returns Current user or null
 */
export function getCurrentUser(): User | null {
  return currentUser;
}

/**
 * Set current user
 * @param user - User object or null
 */
export function setCurrentUser(user: User | null): void {
  currentUser = user;
}

/**
 * Check authentication status and update state
 * @param onComplete - Callback to run after auth check completes
 */
export async function checkAuthStatus(onComplete?: (user: User | null) => void): Promise<void> {
  const user = await checkAuthStatusAPI();
  currentUser = user;

  if (onComplete) {
    onComplete(user);
  }

  // Emit authReady event so other modules can wait for auth to complete
  window.dispatchEvent(new CustomEvent('authReady', { detail: { user } }));
}

/**
 * Initialize auth state on page load
 * Waits for i18n to be ready before checking auth
 * @param onComplete - Callback to run after auth check completes
 */
export function initializeAuth(onComplete?: (user: User | null) => void): void {
  // Wait for i18n to be ready before checking auth (to avoid translation errors)
  if (window.i18n && window.i18n.translations && Object.keys(window.i18n.translations).length > 0) {
    // i18n already loaded
    checkAuthStatus(onComplete);
  } else {
    // Wait for i18n to load
    window.addEventListener('i18nReady', () => {
      checkAuthStatus(onComplete);
    }, { once: true });
  }
}

// Export to window for backwards compatibility
window.getCurrentUser = getCurrentUser;

/**
 * Setup header branding click behavior
 * Only navigates to landing page when not logged in
 */
function setupHeaderBranding(): void {
  const branding = document.getElementById('header-branding');
  if (!branding || branding.hasAttribute('data-click-setup')) return;

  branding.setAttribute('data-click-setup', 'true');

  function updateBrandingState(): void {
    const isLoggedIn = !!getCurrentUser();
    if (isLoggedIn) {
      branding.classList.remove('clickable');
    } else {
      branding.classList.add('clickable');
    }
  }

  branding.addEventListener('click', () => {
    if (!getCurrentUser()) {
      window.location.href = '/';
    }
  });

  updateBrandingState();
  window.addEventListener('authReady', updateBrandingState);
}

// Setup header branding when DOM is ready or partials are loaded
function trySetupHeaderBranding(): void {
  setupHeaderBranding();
  // Retry after short delay in case partials are still loading
  if (!document.getElementById('header-branding')) {
    setTimeout(setupHeaderBranding, 100);
    setTimeout(setupHeaderBranding, 500);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', trySetupHeaderBranding);
} else {
  trySetupHeaderBranding();
}
document.addEventListener('partialLoaded', (e: Event) => {
  const detail = (e as CustomEvent).detail;
  if (detail?.path === 'components/app-header') {
    setupHeaderBranding();
  }
});
